import { useEffect, useRef, useState } from 'react';
import { BuildingOffice2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useInfinitePractices } from '../../../../hooks/useAccounts';
import type { PracticeListItem } from '../../../../services/accounts';
import { SkeletonLine } from '../dm/DmSkeletons';

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_LIMIT = 20;
// Mirrors WorkQueue's SearchDropdown: root is the picker's own scroll
// container (not the viewport) so the observer fires inside a nested panel.
const SENTINEL_ROOT_MARGIN = '60px 0px';

interface PracticeRowProps {
    practice: PracticeListItem;
    isCurrent: boolean;
    onSelect: () => void;
}

function PracticeRow({ practice, isCurrent, onSelect }: PracticeRowProps) {
    const content = (
        <>
            <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-gray-900 truncate">
                    {practice.name}
                </span>
                {practice.doctorName && (
                    <span className="block text-xs text-gray-400 truncate">
                        Dr. {practice.doctorName}
                    </span>
                )}
            </span>
        </>
    );

    if (isCurrent) {
        return (
            <div className="w-full px-3 h-12 flex items-center gap-2.5 text-left opacity-60">
                {content}
                <span className="shrink-0 text-[11px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    Current practice
                </span>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onSelect}
            className="w-full px-3 h-12 flex items-center gap-2.5 text-left hover:bg-gray-50 transition-colors"
        >
            {content}
        </button>
    );
}

function PickerSkeletonRows() {
    return (
        <div className="flex flex-col gap-1 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 flex items-center gap-2.5 px-1">
                    <SkeletonLine className="h-4 w-4 rounded-full shrink-0" />
                    <SkeletonLine className="h-4 w-2/3" />
                </div>
            ))}
        </div>
    );
}

interface PracticePickerListProps {
    /** Rendered as a disabled, tagged row instead of being selectable. */
    currentPracticeId: string | null;
    onSelect: (practice: PracticeListItem) => void;
}

// Search + infinite-scroll practice picker; empty search browses all practices, name-sorted.
function PracticePickerList({ currentPracticeId, onSelect }: PracticePickerListProps) {
    const [inputValue, setInputValue] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(inputValue.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [inputValue]);

    const { practices, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfinitePractices({
            limit: PAGE_LIMIT,
            q: debouncedSearch || undefined,
            sort: 'name',
            order: 'asc'
        });

    useEffect(() => {
        if (!hasNextPage || isFetchingNextPage) return;
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
            },
            { root: scrollRef.current, rootMargin: SENTINEL_ROOT_MARGIN }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, practices.length]);

    return (
        <div className="flex flex-col gap-3">
            <span className="h-11 border border-gray-300 rounded-lg px-3 flex items-center gap-2">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Search practices by name"
                    className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                    autoFocus
                />
            </span>
            <div
                ref={scrollRef}
                className="border border-gray-200 rounded-xl overflow-y-auto divide-y divide-gray-100 max-h-72"
            >
                {isLoading && <PickerSkeletonRows />}
                {!isLoading && (
                    <>
                        {practices.length === 0 && (
                            <div className="px-3 py-2.5 text-sm text-gray-400">
                                No practices match
                            </div>
                        )}
                        {practices.map((practice) => (
                            <PracticeRow
                                key={practice.id}
                                practice={practice}
                                isCurrent={practice.id === currentPracticeId}
                                onSelect={() => onSelect(practice)}
                            />
                        ))}
                        <div ref={sentinelRef} aria-hidden="true" />
                    </>
                )}
                {isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 px-3 py-2.5 text-gray-400">
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin flex-shrink-0" />
                        <span className="text-sm">Loading more…</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export { PracticePickerList };
